import { Component, OnInit, Pipe, ViewChild } from "@angular/core";
import { Location, CurrencyPipe, DatePipe } from "@angular/common";
import { LegendPosition } from "@swimlane/ngx-charts";
import { Router, ActivatedRoute } from "@angular/router";
import { Subject, Observable, Subscription } from "rxjs";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Pair } from "../obj/Pair";
import { AbstractBuyerComponent } from "./abstract/buyer.component";
import { ConfirmationDialog } from "./incs/confirmation-dialog.component";
import { AccountExampleDialog } from "./incs/account-example-dialog.component";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { DateTime } from "luxon";

import { AuthService } from "../service/auth.service";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { AccountData } from "../obj/AccountData";
import { AccountSelectData } from "../obj/AccountSelectData";
import { BuyOffer } from "../entity/BuyOffer";
import { SellOffer } from "../entity/SellOffer";
import { KilowantContract } from "../entity/KilowantContract";
import { User } from "../entity/User";
import { Account } from "../entity/Account";
import { Utility } from "../entity/Utility";
import { LoadZone } from "../entity/LoadZone";
import { RateClass } from "../entity/RateClass";
import { Document } from "../entity/Document";
import { EntityBase } from "../entity/EntityBase";
import { AccountSelection } from "../obj/AccountSelection";
import { HeaderComponent } from "./incs/header.component";
import { AccountEditComponent } from "./incs/account-edit.component";
import { OneClickDirective } from "./helpers/one-click.component";

@Component({
    selector: "app-selloffer-details",
    templateUrl: "../../html/selloffer-details.component.html",
    imports: [HeaderComponent, AccountEditComponent, OneClickDirective, CurrencyPipe, DatePipe]
})

export class SellOfferDetailsComponent extends AbstractBuyerComponent {

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;

    step: number = 1;
    disableStep1: boolean = false;
    offer: SellOffer;
    relatedContract: KilowantContract = null!;
    showSellerContractTerms: boolean = false;
    accounts: AccountData[] = [];
    currentAccountStep: number = -1;
    account: Account = null!;
    buttonDisable: boolean = false;
    viewAccounts: boolean = false;

    processingUser: User;
    processTaxExempt: boolean = false;
    fileToUpload: File | null = null;

    offerId: number;
    accountId: number;

    accountsSelector: AccountSelection[] = [];
    selectedAccounts: (Account | null)[] = [];
    selectedAccountsIds: number[];
    selectedAccountsChanged: boolean[] = [];

    callUpdate: Subject<boolean> = new Subject();
    callCancel: Subject<boolean> = new Subject();

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog,
        private _location: Location
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.user.isAdvisor) {
            let u = this.generalService.getStoredClient();
            if (u != null) {
                this.processingUser = u;
            }
        } else {
            this.processingUser = this.user;
        }

        if (this.route.snapshot.paramMap.get("id") != null) {
            this.offerId = Number(this.route.snapshot.paramMap.get("id"));
        }
        if (this.route.snapshot.paramMap.get("accountId") != null) {
            this.accountId = Number(this.route.snapshot.paramMap.get("accountId"));
        }
        this.refresh();
    }

    refresh(): void {
        this.generalService.getAccount(this.accountId).then((a) => {
            this.account = a;
            this.openExampleDialog();
            this.generalService.getSellOfferPerAccount(this.account, this.offerId).then((ele) => {
                this.offer = ele.key;
                this.relatedContract = ele.value;
                if (this.relatedContract != null) {
                  this.router.navigate(["/view-priceplan/" + this.relatedContract.id]);
                    return;
                }
                this.generalService.getAccountsNonUsedPerPeriod(this.offer, null, null, this.account).then((accountsSelector) => {
                    this.accountsSelector = accountsSelector;
                    this.selectedAccounts = [];
                    this.selectedAccountsIds = [];
                    this.selectItem(this.account!.id);
                    let groupPos = this.getSelectedGroupPosition();
                    if (groupPos >= 0) {
                        for (let j = 0; j < this.accountsSelector[groupPos].accountData.length; j++) {
                            this.addRemoveAccount(this.accountsSelector[groupPos].accountData[j].id, true);
                        }
                    }
                    if (this.accounts.length == 1) {
                        this.step = 2;
                        this.disableStep1 = true;
                    }
                });
            });
        });
    }

    ifItemSelected(accountId: number): boolean {
        let pos = this.selectedAccountsIds.indexOf(accountId);
        return (pos >= 0);
    }

    ifItemSelectable(accountId: number): boolean {
        if (this.ifItemSelected(accountId)) {
            return true;
        }
        if (this.selectedAccounts.length == 0) {
            return true;
        }
        let selectedGroup = this.getSelectedGroupPosition();
        if (selectedGroup == -1) {
            return true;
        }
        for (let j = 0; j < this.accountsSelector[selectedGroup].accountData.length; j++) {
            if (this.accountsSelector[selectedGroup].accountData[j].id == accountId) {
                return true;
            }
        }
        return false;
    }

    getAccountFromSelector(accountId: number): Account | null {
        for (let i = 0; i < this.accountsSelector.length; i++) {
            for (let j = 0; j < this.accountsSelector[i].accountData.length; j++) {
                if (this.accountsSelector[i].accountData[j].id == accountId) {
                    return this.accountsSelector[i].accountData[j].account;
                }
            }
        }
        return null;
    }

    getSelectedGroupPosition(): number {
        for (let i = 0; i < this.accountsSelector.length; i++) {
            for (let j = 0; j < this.accountsSelector[i].accountData.length; j++) {
                if (this.ifItemSelected(this.accountsSelector[i].accountData[j].id)) {
                    return i;
                }
            }
        }
        return -1;
    }

    selectItem(accountId: number): void {
        if (this.ifItemSelected(accountId)) {
            this.addRemoveAccount(accountId, false);
        } else if (this.ifItemSelectable(accountId)) {
            this.addRemoveAccount(accountId, true);
        }
        this.changeCurrentAccountStep(1);
    }

    changeCurrentAccountStep(newStep: number): void {
        if (this.selectedAccounts[newStep - 1] == null) {
            this.generalService.getAccount(this.selectedAccountsIds[newStep - 1]).then(account => {
                this.selectedAccounts[newStep - 1] = account;
                this.currentAccountStep = newStep;
            });
        }
    }

    addRemoveAccount(accountId: number, adding: boolean): void {
        let pos = this.selectedAccountsIds.indexOf(accountId);
        if (adding && pos == -1) {
            var account = this.getAccountFromSelector(accountId);
            if (account != null) {
                this.selectedAccountsIds.push(accountId);
                this.selectedAccounts.push(account);
                this.selectedAccountsChanged.push(false);
            }
        }
        if (!adding && pos >= 0) {
            this.selectedAccountsIds.splice(pos, 1);
            this.selectedAccounts.splice(pos, 1);
            this.selectedAccountsChanged.splice(pos, 1);
        }
        if (this.selectedAccounts.length > 0) {
            this.clearErrors();
        }
    }

    getTermTo(termFrom: Date, termPeriod: number): Date {
        var d = new Date(termFrom);
        return new Date(d.setMonth(d.getMonth() + termPeriod));
    }

    updateAccount(): void {
        this.callUpdate.next(true);
    }

    changeCurrentAccountInfo(): void {
        if (this.selectedAccountsChanged[this.currentAccountStep - 1]) {
            return;
        }
        this.selectedAccountsChanged[this.currentAccountStep - 1] = true;
    }

    editFinish(): void {
        this.selectedAccountsChanged[this.currentAccountStep - 1] = false;
        if (this.currentAccountStep < this.selectedAccounts.length) {
            this.changeCurrentAccountStep(this.currentAccountStep+1);
            return;
        } else {
            this.editFinishAfter();
        }
    }

    editFinishAfter(): void {
        window.scrollTo(0, 0);
        this.clearErrors();
        this.step++;
    }

    changeStep(p: number): void {
        if (this.selectedAccountsChanged[this.currentAccountStep - 1]) {
            return;
        }
        this.changeCurrentAccountStep(p);
        window.scrollTo(0, 0);
        this.clearErrors();
    }

    formatSelectedAccounts(): Pair[] {
        let res: Pair[] = [];
        let useAccounts = this.selectedAccounts;
        let utility = this.offer.utility;
        let loadZones = this._getLoadZones(useAccounts);
        let rateClasses = this._getRateClasses(useAccounts);
        for (let x = 0; x < loadZones.length; x++) {
            for (let y = 0; y < rateClasses.length; y++) {
                res.push(new Pair(utility.name + " > " + (<LoadZone>loadZones[x]).name + " > " + (<RateClass>rateClasses[y]).name, []));
                for (var z = 0; z < useAccounts.length; z++) {
                    if (useAccounts[z]!.utility != null && useAccounts[z]!.loadZone != null && useAccounts[z]!.rateClass != null) {
                        if (((<LoadZone>useAccounts[z]!.loadZone).id == loadZones[x].id) &&
                            ((<RateClass>useAccounts[z]!.rateClass).id == rateClasses[x].id)) {
                            res[res.length - 1].value.push(useAccounts[z]);
                        }
                    }
                }
            }
        }
        return res;
    }

    cancel() {
        this.callCancel.next(true);
        this._location.back();
    }

    acceptSellOffer(): void {
        if (this.selectedAccounts.length == 0) {
            this.setErrorText("You need to select at least one account");
            return;
        }
        this.acceptProcess();
    }

    acceptProcess(): void {
        this.generalService.acceptSellOffer(this.offer, this.selectedAccountsIds, false).subscribe((newContractId) => {
            if (newContractId != null && newContractId > 0) {
                this.openResultsPopup("Your New Price Plan is Pending", "A confirmation email has been sent to " + this.user.email + " including the details of your accepted price plan. ", null);
                this.router.navigate(["/user/home"], { queryParams: { confirm: true } });
                return;
            }
        });
    }

    finishStep1(): void {
        if (this.selectedAccounts.length == 0) {
            this.setErrorText("You need to select at least one account");
            return;
        }
        for (let i = 0; i < this.selectedAccounts.length; i++) {
            for (let x = 0; x < this.accounts.length; x++) {
                if ((this.selectedAccounts[i]!.id == this.accounts[x].account.id) && (this.accounts[x].inBuyOffer)) {
                    this.confirmAccountUsedInBuyOffer();
                    return;
                }
            }
        }
        this.clearErrors();
        this.step++;
    }

    openExampleDialog(): void {
        this.dialog.open(AccountExampleDialog, {
            disableClose: false
            , panelClass: "example-popup"
        });
    }

    confirmAccountUsedInBuyOffer(): void {
        this.dialogRef = this.dialog.open(ConfirmationDialog, {
            disableClose: false
            , panelClass: "i-icon-popup"
        });
        this.dialogRef.componentInstance.confirmTitle = "Confirm";
        this.dialogRef.componentInstance.confirmMessage = "You have selected an account that is associated with an open Buyer Offer. Please confirm that you wish to override your Buyer Offer and proceed with acceptance of the selected Seller Offer."

        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.clearErrors();
                this.step++;
            }
            this.dialogRef = null!;
        });
    }

    sendTerms(): void {
        document.location.href = "/pdf/" + this.offer.sellerContract.termsDocument.value;
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.resetError("fileToUpload");
    }

    deleteFile() {
        this.fileToUpload = null;
    }

    downloadFile() {
        this.generalService.sendFileToClient(this.fileToUpload);
    }
}
